
import { Button } from '@/components/ui/button';
import { Product } from '@/types';
import { Link } from 'react-router-dom';
import { ProductCard } from './ProductCard';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { Loader2 } from 'lucide-react';
import { getDefaultDisplayVariant, DisplayVariantInfo } from '@/utils/variantSelection';
import { reportError } from '@/utils/errorReporter';
import { getTimeoutSignal, retryOnTransientNetwork } from '@/utils/networkResilience';

type FeaturedProduct = Product & {
  defaultVariant?: DisplayVariantInfo | null;
};

const fetchFeaturedProducts = async (): Promise<FeaturedProduct[]> => {
  return retryOnTransientNetwork(async () => {
    const { signal, clear } = getTimeoutSignal();

    type ProductWithRelations = Database['public']['Tables']['products']['Row'] & {
      product_categories: { categories: { id: string; name: string } | null }[];
      product_variants: (Database['public']['Tables']['product_variants']['Row'] & {
        variant_images: Database['public']['Tables']['variant_images']['Row'][];
      })[];
    };

    const { data, error } = await supabase
      .from("products")
      .select<string, ProductWithRelations>(`
        *, 
        product_categories(categories(id, name)), 
        product_variants(
          *,
          variant_images(*)
        )
      `)
      .eq('is_published', true)
      .eq('is_featured', true)
      .order('created_at', { ascending: false })
      .limit(4)
      .abortSignal(signal);
      
    clear();
    if (error) {
      const err = new Error(error.message);
      reportError('Homepage/FeaturedProducts', err);
      throw err;
    }
    
    if (!data) return [];

  const products = data.map(p => {
    const { product_categories, product_variants, ...rest } = p;
    const categories = (product_categories || []).map(pc => pc.categories).filter(Boolean) as {id: string, name: string}[];
    const categoryNames = categories.map(c => c.name);
    
    // Enhance variants with cover images
    const enhancedVariants = (product_variants || []).map(variant => {
      const coverImage = variant.variant_images?.find(img => img.is_cover);
      const sortedImages = [...(variant.variant_images || [])].sort((a, b) => 
        (a.sort_order || 0) - (b.sort_order || 0)
      );
      const firstImage = sortedImages[0];
      const galleryImageUrl = (coverImage || firstImage)?.image_url;
      
      return {
        ...variant,
        image_url: galleryImageUrl || variant.image_url
      };
    });
    
    const defaultVariant = getDefaultDisplayVariant(enhancedVariants);
    return {
      ...rest, 
      categories, 
      category: categoryNames,
      defaultVariant,
      is_discounted: rest.is_discounted || false,
      discount_price: rest.discount_price || undefined
    };
  });

    return products as FeaturedProduct[];
  });
};

const FeaturedProducts = () => {
  const { data: products, isLoading, error } = useQuery<FeaturedProduct[]>({
    queryKey: ["featured-products"],
    queryFn: fetchFeaturedProducts,
  });

  const numProducts = products?.length || 0;
  
  const gridColsClass = {
    1: 'sm:grid-cols-1 lg:grid-cols-1 lg:max-w-xs mx-auto',
    2: 'sm:grid-cols-2 lg:grid-cols-2 lg:max-w-2xl mx-auto',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
  }[numProducts] || 'sm:grid-cols-2 lg:grid-cols-4';

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold">Meet Your New Best Buds</h2>
          <p className="mt-4 text-lg text-muted-foreground">Hand-picked favorites to get you started.</p>
        </div>
        <div className={`mt-12 grid grid-cols-1 gap-x-6 gap-y-10 ${gridColsClass}`}>
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex flex-col space-y-3">
                <div className="h-[300px] w-full rounded-xl bg-muted animate-pulse" />
                <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
                <div className="h-4 w-1/4 rounded bg-muted animate-pulse" />
              </div>
            ))
          ) : error ? (
            <p className="col-span-full text-center text-destructive">Could not load featured products.</p>
          ) : (
            products?.map((product) => {
              const variant = product.defaultVariant;
              const displayImage = variant?.imageUrl || product.image_urls?.[0] || "/placeholder.svg";
              const displayPrice = variant?.price || product.price;
              const discountPrice = variant?.isDiscounted && variant?.discountPrice ? variant.discountPrice : null;
              const isDiscounted = variant?.isDiscounted || false;

              return (
                <ProductCard 
                  key={product.id} 
                  id={product.id}
                  name={product.name}
                  price={displayPrice}
                  image={displayImage}
                  discountPrice={discountPrice || undefined}
                  isDiscounted={isDiscounted}
                />
              );
            })
          )}
        </div>
        <div className="mt-16 text-center">
          <Button size="lg" variant="outline" asChild>
            <Link to="/shop">View All Plants</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
